import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Banner, Card, Skeleton } from '@shared/design-system/ui'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { toast } from '@shared/lib/toast/toast-store'
import {
  updateCheckoutExperience,
  useAdminCheckoutExperience,
  type CheckoutExperienceMode,
} from '../api/admin.api'
import * as s from './AdminCheckoutExperienceScreen.css'

const MODE_OPTIONS: Array<{
  value: CheckoutExperienceMode
  title: string
  description: string
}> = [
  {
    value: 'FORCE_NEW',
    title: 'Forçar Checkout 1',
    description: 'Todos os compradores veem o visual atual da Nova VendePay.',
  },
  {
    value: 'FORCE_LEGACY',
    title: 'Forçar Checkout 2',
    description: 'Todos os compradores veem o skin modern da antiga. Use como kill switch.',
  },
  {
    value: 'RANDOM',
    title: 'Aleatório (teste A/B)',
    description: 'Sorteia o visitante e mantém a mesma variante. A conversão aparece abaixo.',
  },
]

export function AdminCheckoutExperienceScreen() {
  const session = useSession((state) => state.user)
  const canRead = hasAdminPermission(session, 'CHECKOUT_EXPERIENCE_READ')
  const canManage = hasAdminPermission(session, 'CHECKOUT_EXPERIENCE_MANAGE')
  const queryClient = useQueryClient()
  const experience = useAdminCheckoutExperience(canRead)
  const update = useMutation({
    mutationFn: updateCheckoutExperience,
    onSuccess: (snapshot) => {
      queryClient.setQueryData(['admin-checkout-experience'], snapshot)
      toast.success('Experiência do checkout atualizada.')
    },
    onError: () => {
      toast.error('Não foi possível atualizar a experiência do checkout.')
    },
  })

  if (!canRead) {
    return (
      <Banner tone="warning" title="Sem permissão">
        Você precisa de CHECKOUT_EXPERIENCE_READ para consultar o experimento do checkout.
      </Banner>
    )
  }

  if (experience.isLoading) {
    return <Skeleton height={240} />
  }

  if (experience.isError || experience.data === undefined) {
    return (
      <Banner tone="danger" title="Falha ao carregar">
        Não foi possível ler o modo atual do checkout.
      </Banner>
    )
  }

  const snapshot = experience.data

  return (
    <section className={s.page} data-testid="checkout-experience-screen">
      <div className={s.header}>
        <h1 className={s.title}>Experiência do checkout</h1>
        <p className={s.subtitle}>
          Controle global do skin do checkout público. O vendedor não escolhe quem vê cada variante.
          Ele personaliza o Checkout 1 e o Checkout 2 em separado, e cada comprador vê o estilo do
          checkout que recebeu.
        </p>
      </div>

      <Card>
        <div className={s.options}>
          {MODE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={s.option}
              data-active={snapshot.mode === option.value}
              disabled={!canManage || update.isPending}
              onClick={() => {
                if (snapshot.mode === option.value) {
                  return
                }
                update.mutate({ mode: option.value })
              }}
            >
              <p className={s.optionTitle}>{option.title}</p>
              <p className={s.optionCopy}>{option.description}</p>
            </button>
          ))}
        </div>
      </Card>

      <div className={s.metrics}>
        {snapshot.metrics.map((metric) => {
          const conversion =
            metric.viewed === 0 ? '-' : `${((metric.paid / metric.viewed) * 100).toFixed(1)}%`
          return (
            <Card key={metric.variant}>
              <div className={s.metricCard}>
                <h2 className={s.metricTitle}>
                  {metric.variant === 'new' ? 'Checkout 1' : 'Checkout 2'}
                </h2>
                <div className={s.metricGrid}>
                  <div className={s.metricValue}>
                    <span className={s.metricLabel}>Acessos</span>
                    <p className={s.metricNumber}>{metric.accessed}</p>
                  </div>
                  <div className={s.metricValue}>
                    <span className={s.metricLabel}>Visualizações do teste</span>
                    <p className={s.metricNumber}>{metric.viewed}</p>
                  </div>
                  <div className={s.metricValue}>
                    <span className={s.metricLabel}>Pagos</span>
                    <p className={s.metricNumber}>{metric.paid}</p>
                  </div>
                  <div className={s.metricValue}>
                    <span className={s.metricLabel}>Conversão</span>
                    <p className={s.metricNumber}>{conversion}</p>
                  </div>
                </div>
                <p className={s.optionCopy}>Abandonos do teste: {metric.abandoned}</p>
              </div>
            </Card>
          )
        })}
      </div>

      <p className={s.optionCopy}>
        Acessos contam toda visita ao checkout, inclusive com o layout forçado. Visualizações,
        pagos, abandonos e conversão consideram só o modo Aleatório.
      </p>
    </section>
  )
}
